/**
 * «Что выделяется» (доска Campaign): the few things on a campaign worth a
 * buyer's next click, found in the same numbers the report shows. Every check
 * is a plain rule over registry metrics — no score, no model — so the line it
 * prints can be checked against the report it links to. Pure.
 */
import type { Bag } from "@/lib/campaigns-table";

export type InsightKind = "expensive_ad" | "weak_lander" | "night_rejects" | "stale_hold";

export interface Insight {
  kind: InsightKind;
  tone: "danger" | "warn" | "neutral";
  /** Values the dictionary line is filled with. */
  vars: Record<string, string | number>;
  /** Raw numbers the line is about (for ordering and tests). */
  weight: number;
}

export interface Row {
  key: string;
  label: string;
  v: Bag;
}

/** An ad (creative) whose cost per approve is far above the campaign's. */
export function expensiveAd(ads: Row[], campaign: Bag): Insight | undefined {
  const cpa = campaign.cpa;
  if (!cpa || !Number.isFinite(cpa)) return undefined;
  let worst: { row: Row; cpa: number } | undefined;
  for (const r of ads) {
    const cost = r.v.cost ?? 0;
    if (cost <= 0 || !r.key) continue;
    const approvals = r.v.conversions ?? 0;
    // An ad that spent twice the campaign's CPA without one approve is as
    // expensive as one approve at that price — count it so.
    const adCpa = approvals > 0 ? cost / approvals : cost >= 2 * cpa ? cost : undefined;
    if (adCpa === undefined || adCpa < 1.5 * cpa) continue;
    if (!worst || adCpa - cpa > worst.cpa - cpa) worst = { row: r, cpa: adCpa };
  }
  if (!worst) return undefined;
  return {
    kind: "expensive_ad",
    tone: "danger",
    weight: worst.row.v.cost ?? 0,
    vars: {
      ad: worst.row.label,
      cpa: Math.round(worst.cpa),
      campaignCpa: Math.round(cpa),
      cost: Math.round(worst.row.v.cost ?? 0),
      profit: Math.round(worst.row.v.profit ?? 0),
    },
  };
}

/**
 * Two landers on comparable traffic where one converts the button far worse:
 * LP CTR at most 80% of the best, both with at least 100 shows.
 */
export function weakLander(landers: Row[]): Insight | undefined {
  const fair = landers.filter((r) => (r.v.lander_shows ?? 0) >= 100 && r.v.lp_ctr !== undefined);
  if (fair.length < 2) return undefined;
  const sorted = [...fair].sort((a, b) => (b.v.lp_ctr ?? 0) - (a.v.lp_ctr ?? 0));
  const best = sorted[0]!;
  const worst = sorted[sorted.length - 1]!;
  if ((worst.v.lp_ctr ?? 0) > 0.8 * (best.v.lp_ctr ?? 0)) return undefined;
  return {
    kind: "weak_lander",
    tone: "warn",
    weight: worst.v.lander_shows ?? 0,
    vars: {
      worst: worst.label,
      best: best.label,
      worstCtr: Math.round((worst.v.lp_ctr ?? 0) * 100),
      bestCtr: Math.round((best.v.lp_ctr ?? 0) * 100),
    },
  };
}

/**
 * Night leads (00–06 in the workspace's zone) that the partner refuses far
 * more often than the rest: at least 10 night leads, a refusal share 15 points
 * above the day's.
 */
export function nightRejects(hours: Row[]): Insight | undefined {
  let nightLeads = 0;
  let nightRejected = 0;
  let leads = 0;
  let rejected = 0;
  for (const r of hours) {
    const h = Number.parseInt(r.key, 10);
    const l = r.v.leads ?? 0;
    const rej = (r.v.rejected ?? 0) + (r.v.trash ?? 0);
    leads += l;
    rejected += rej;
    if (Number.isFinite(h) && h >= 0 && h < 6) {
      nightLeads += l;
      nightRejected += rej;
    }
  }
  if (nightLeads < 10 || leads === 0) return undefined;
  const nightShare = nightRejected / nightLeads;
  const dayLeads = leads - nightLeads;
  const dayShare = dayLeads > 0 ? (rejected - nightRejected) / dayLeads : 0;
  if (nightShare - dayShare < 0.15) return undefined;
  return {
    kind: "night_rejects",
    tone: "warn",
    weight: nightLeads,
    vars: {
      share: Math.round((nightLeads / leads) * 100),
      rejected: Math.round(nightShare * 100),
    },
  };
}

/** Leads that have waited in hold longer than three days. */
export function staleHold(holdAgesSeconds: number[], payoutUsd: number[], network: string): Insight | undefined {
  let n = 0;
  let sum = 0;
  holdAgesSeconds.forEach((age, i) => {
    if (age > 3 * 86_400) {
      n++;
      sum += payoutUsd[i] ?? 0;
    }
  });
  if (n === 0) return undefined;
  return { kind: "stale_hold", tone: "neutral", weight: n, vars: { count: n, sum: Math.round(sum), network } };
}

/** «до 24 ч · 1–3 дня · больше 3 дней» — how long the hold has waited. */
export function holdBuckets(holdAgesSeconds: number[]): [number, number, number] {
  const out: [number, number, number] = [0, 0, 0];
  for (const age of holdAgesSeconds) {
    if (age <= 86_400) out[0]++;
    else if (age <= 3 * 86_400) out[1]++;
    else out[2]++;
  }
  return out;
}
